/**
 * Resolução e persistência do idioma.
 *
 * Ordem, nesta ordem:
 *   1. o que a pessoa escolheu, em localStorage["lesvita_lang"]
 *   2. o idioma do aparelho, reduzido a pt ou en
 *   3. pt-BR
 *
 * Sem detector automático de biblioteca: a regra é curta e explícita, e um
 * detector agressivo já causou o bug de um aparelho que reporta "en" não
 * casar com "en-US" e cair no fallback errado.
 *
 * IMPORTANTE: isto muda só a exibição. Toda regra de negócio que depende de
 * data continua em America/Sao_Paulo (virada de ofensiva, competição, dia da
 * refeição). Ver src/utils/streakStatus.js.
 */

export const IDIOMAS = ['pt-BR', 'en'];
export const PADRAO = 'pt-BR';
export const CHAVE_STORAGE = 'lesvita_lang';

/** Reduz qualquer tag BCP-47 a um dos idiomas suportados, ou null. */
export function normalizar(tag) {
  if (!tag || typeof tag !== 'string') return null;
  const base = tag.toLowerCase().split(/[-_]/)[0];
  if (base === 'pt') return 'pt-BR';
  if (base === 'en') return 'en';
  return null;
}

/** O que está salvo, se for válido. */
export function salvo() {
  try {
    return normalizar(localStorage.getItem(CHAVE_STORAGE));
  } catch {
    return null; // modo privado, storage bloqueado
  }
}

/** Idioma do aparelho pelo que o WebView informa (síncrono). */
export function doAparelho() {
  try {
    const lista = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
    for (const tag of lista) {
      const n = normalizar(tag);
      if (n) return n;
    }
  } catch {
    /* ignora */
  }
  return null;
}

/** Resolução síncrona, usada no boot. */
export function resolver() {
  return salvo() || doAparelho() || PADRAO;
}

export function persistir(idioma) {
  try {
    localStorage.setItem(CHAVE_STORAGE, idioma);
  } catch {
    /* sem storage: vale só para esta sessão */
  }
}

/**
 * Confere o idioma do sistema pelo plugin nativo.
 *
 * Só faz diferença quando a pessoa nunca escolheu e o WebView reporta algo
 * diferente do sistema. É assíncrono, então roda depois do boot e corrige
 * se preciso — nunca atrasa a primeira pintura.
 *
 * @returns {Promise<string|null>} idioma corrigido, ou null se nada muda
 */
export async function conferirNoAparelho() {
  if (salvo()) return null; // escolha da pessoa manda

  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor.isNativePlatform()) return null;

    const { Device } = await import('@capacitor/device');
    const { value } = await Device.getLanguageCode();
    return normalizar(value);
  } catch {
    return null; // plugin ausente ou falhou: fica o que já foi resolvido
  }
}
